/**
 * A one-line, readable rendering of a rule expression, for the rule label on the canvas.
 *
 * NOT a paraphrase. `humanCondition` (projection.ts) refuses anything beyond the two
 * dominant shapes because an invented sentence could misstate the rule; this keeps the
 * expression's own structure and only SUBSTITUTES the parts that have an exact name:
 *
 *   metric['Q_1_1_1'] === 'a'          → Q1.1.1 =「全球区域」
 *   metric['Q_1_1_3_1'].includes('f')  → Q1.1.3.1 含「R-PPA」
 *   hasAnyMatch('Q_1_1_3_1', [...])    → Q1.1.3.1 任一
 *   hasKey('X') / !hasKey('X')         → 已授予「X」/ 未授予「X」
 *   metric['Q_1_1_2_a1']               → Q1.1.2_a1
 *   && / ||                            → 且 / 或
 *
 * Arithmetic, comparison operators and parentheses pass through untouched — so a precedence
 * bug like `a || b + c || d` stays visibly wrong instead of being smoothed into prose. The
 * raw expression is always one hover away on the label.
 */
import type { QuestionnaireDefinition, QuestionnaireNode } from '../types';

export interface SummaryLookup {
  nodeIdOf: (qualifiedId: string) => string | undefined;
  optionLabelOf: (qualifiedId: string, value: string) => string | undefined;
}

export function summaryLookupOf(def: QuestionnaireDefinition): SummaryLookup {
  const byQ = new Map<string, QuestionnaireNode>();
  for (const n of def.nodes) if (n.qualifiedNodeId) byQ.set(n.qualifiedNodeId, n);
  return {
    nodeIdOf: (q) => byQ.get(q)?.nodeId,
    optionLabelOf: (q, v) => {
      const o = byQ.get(q)?.answerOptions?.find((x) => x.value === v);
      return o ? o.optionText.cn || o.optionText.en || undefined : undefined;
    },
  };
}

export function ruleSummary(expression: string, look: SummaryLookup): string {
  const id = (q: string) => look.nodeIdOf(q) ?? q;
  const opt = (q: string, v: string) => look.optionLabelOf(q, v) ?? v;
  let s = expression.trim();
  if (s === 'true') return '否则';
  s = s.replace(/metric\[\s*'([^']+)'\s*\]\.includes\(\s*'([^']*)'\s*\)/g, (_m, q, v) => `${id(q)} 含「${opt(q, v)}」`);
  s = s.replace(/metric\[\s*'([^']+)'\s*\]\s*!==?\s*'([^']*)'/g, (_m, q, v) => `${id(q)} ≠「${opt(q, v)}」`);
  s = s.replace(/metric\[\s*'([^']+)'\s*\]\s*={2,3}\s*'([^']*)'/g, (_m, q, v) => `${id(q)} =「${opt(q, v)}」`);
  s = s.replace(/hasAnyMatch\(\s*'([^']+)'\s*,\s*\[[^\]]*\]\s*\)/g, (_m, q) => `${id(q)} 任一`);
  s = s.replace(/!\s*hasKey\(\s*'([^']+)'\s*\)/g, (_m, p) => `未授予「${p}」`);
  s = s.replace(/hasKey\(\s*'([^']+)'\s*\)/g, (_m, p) => `已授予「${p}」`);
  s = s.replace(/metric\[\s*'([^']+)'\s*\]/g, (_m, q) => id(q));
  s = s.replace(/\s*&&\s*/g, ' 且 ').replace(/\s*\|\|\s*/g, ' 或 ');
  return s.replace(/\s+/g, ' ').trim();
}
